import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { LicenseService } from 'app/shared/services/license.service';
import { License } from 'app/shared/models/license';
import { User } from 'app/shared/models/user';
import {
  AuthService,
  Destroy,
  DialogResult,
  DialogService,
  Key,
  KpdNavigator,
  SearchConfig,
  SearchExpression,
  SecureActionDialogData,
  tr,
  Types,
  ViewMode,
} from '@kpd/framework-ngx';
import { ActivatedRoute } from '@angular/router';
import { of, zip } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { UserFacade } from 'app/shared/services/facades/user.facade';
import { Permissions } from 'app/shared/services/permissions';
import { flattenArrays } from 'app/shared/utils/array';

export interface ILicenseDetails {
  loading: boolean;
  hasLicense: boolean;
  license: License;
  error?: string;
}

@Component({
  selector: 'app-user-details-licenses',
  templateUrl: './user-details-licenses.component.html',
})
export class UserDetailsLicensesComponent implements OnChanges, OnDestroy {
  Permissions: typeof Permissions = Permissions;
  licenses: ILicenseDetails[] = [];
  filteredLicenses: ILicenseDetails[] = [];
  loading = false;
  readOnlyMode = true;
  @Input()
  item: User = new User();
  @Output()
  switchMode = new EventEmitter<string>();
  @Output()
  itemChanged = new EventEmitter<number>();
  @Destroy()
  destroy$: any;
  showExpired = false;

  searchConfig = this.getSearchConfig();
  itemsTotalCount: number;
  searchExpression: SearchExpression;

  get activeLicenses(): ILicenseDetails[] {
    return this.filteredLicenses.filter((l) => !l.license.isExpired);
  }

  get expiredLicenses(): ILicenseDetails[] {
    return this.filteredLicenses.filter((l) => l.license.isExpired);
  }

  get allApplications(): {
    name: string;
    description?: string;
  }[] {
    const arrayOfApplications = flattenArrays(this.licenses.map((l) => l.license.applications || []));
    const arrayOfApplicationNames = arrayOfApplications.map((a) => a.name);
    const uniqueNames = Array.from(new Set(arrayOfApplicationNames));
    return uniqueNames.map((name) => {
      const app = arrayOfApplications.find((a) => a.name === name);
      const display = `${name} ${app && app.description ? ' - ' + app.description : ''}`;
      return {
        name: name,
        display: display,
      };
    });
  }

  get hasSystemUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.SystemUserManage);
  }

  get isSystemUser(): boolean {
    return this.item.isSystemUser;
  }

  constructor(
    private licenseService: LicenseService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userFacade: UserFacade,
    private kpdNavigator: KpdNavigator,
    private dialogService: DialogService,
  ) {
    this.userFacade.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((load) => (this.loading = load['detailLoading'] && load['detailLoading'].length > 0));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item && !!changes.item.currentValue && !!changes.item.currentValue.key) {
      if (
        this.route.snapshot.queryParams['mode'] === ViewMode.Edit &&
        this.authService.getIdentity().hasPermission(Permissions.UserLicenseUpdate) &&
        (!this.isSystemUser || this.hasSystemUserManagePermission)
      ) {
        this.readOnlyMode = false;
      }
      this.getLicenses();
    }
  }

  private getLicenses(): void {
    this.userFacade.detailLoadingStarted();
    if (this.readOnlyMode) {
      this.getUserLicenses();
    } else {
      this.createLicenses();
    }
  }

  createLicenses(): void {
    if (!this.item.upn) {
      return;
    }
    zip(this.licenseService.getLicenses(), this.licenseService.getLicensesForUser(this.item.upn))
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: Array<License[]>) => {
        const tenantLicenses = result[0];
        const userLicenses = result[1];

        const licenses = tenantLicenses.map((tenantLicense) => {
          const hasLicense = userLicenses.some((userLicense) => userLicense.licenseId === tenantLicense.licenseId);
          return { loading: false, hasLicense, license: tenantLicense };
        });

        this.licenses = licenses;
        this.filteredLicenses = licenses;

        this.userFacade.detailLoadingFinished();
      });
  }

  private getUserLicenses(): void {
    if (!this.item.upn) {
      return;
    }
    this.licenseService.getLicensesForUser(this.item.upn).subscribe((licenses) => {
      const licenseDetails = licenses.map((license) => ({ loading: false, hasLicense: true, license }));
      this.licenses = licenseDetails;
      this.filteredLicenses = licenseDetails;
      this.userFacade.detailLoadingFinished();
    });
  }

  onToggle(licenseDetails: ILicenseDetails): void {
    licenseDetails.loading = true;

    if (licenseDetails.hasLicense) {
      this.licenseService
        .createUserLicense(licenseDetails.license.key, this.item.upn as string)
        .pipe(
          tap(() => {
            licenseDetails.error = undefined;
          }),
          catchError((error) => {
            if (error.status !== 409 && error.status !== 404) {
              licenseDetails.hasLicense = !licenseDetails.hasLicense;
              licenseDetails.error = tr(
                'Something went wrong saving this license, please refresh your page to see if your changes are saved!',
              );
              licenseDetails.license.quantityAssigned -= 1;
            }
            return of(licenseDetails.hasLicense);
          }),
        )
        .subscribe(() => {
          licenseDetails.license.quantityAssigned += 1;
          let total = 0;
          this.licenses.forEach((l) => {
            total += l.hasLicense && !l.license.isExpired ? 1 : 0;
          });
          this.itemChanged.emit(total);
          licenseDetails.loading = false;
        });
    } else {
      if (this.isSystemUser) {
        const messages = [
          {
            text: tr('Are you sure you want to remove this license from a System User?'),
          },
        ];
        const confirmData: SecureActionDialogData = {
          header: tr(`Remove {1} from {2} ?`)
            .replace('{1}', `${tr('License')} ${licenseDetails.license.name}`)
            .replace('{2}', ` ${this.item.displayName}`),
          messages: messages,
          cancelButtonText: tr('Cancel'),
          verifyButtonText: tr('Remove'),
          verifyButtonStyle: 'danger',
          verificationString: licenseDetails.license.name.trim(),
          verificationLabel: tr('Type the name of the {value} to confirm').replace(/{.*}/, `${'license'}`),
        };
        this.dialogService
          .open('secureDelete', confirmData)
          .afterClosed()
          .subscribe((result: DialogResult) => {
            if (result === DialogResult.Verify) {
              this.deleteUserLicense(licenseDetails);
              return true;
            } else {
              licenseDetails.hasLicense = true;
              licenseDetails.loading = false;
              return false;
            }
          });
      } else {
        this.deleteUserLicense(licenseDetails);
      }
    }
  }

  private deleteUserLicense(licenseDetails: ILicenseDetails) {
    this.licenseService
      .deleteUserLicense(licenseDetails.license.key, this.item.upn as string)
      .pipe(
        tap(() => {
          licenseDetails.error = undefined;
        }),
        catchError((error) => {
          if (error.status !== 409 && error.status !== 404) {
            licenseDetails.hasLicense = !licenseDetails.hasLicense;
            licenseDetails.error = tr(
              'Something went wrong saving this license, please refresh your page to see if your changes are saved!',
            );
            licenseDetails.license.quantityAssigned += 1;
          }
          return of(licenseDetails.hasLicense);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        licenseDetails.license.quantityAssigned -= 1;
        let total = 0;
        this.licenses.forEach((l) => {
          total += l.hasLicense && !l.license.isExpired ? 1 : 0;
        });
        this.itemChanged.emit(total);
        licenseDetails.loading = false;
      });
  }

  toggleViewMode(): void {
    this.readOnlyMode = !this.readOnlyMode;
    this.getLicenses();
    this.switchMode.emit(this.readOnlyMode ? ViewMode.Consult : ViewMode.Edit);
  }

  ngOnDestroy(): void {
    // Needed for custom destroy decorator
  }

  onClickAssigned(key: Key<string>): void {
    this.kpdNavigator.navigateChild(['licenses', 'details', 'license', key.id], {
      queryParams: { tab: 'users' },
    });
  }
  getSearchConfig(): SearchConfig {
    return new SearchConfig({
      multipleCriteria: true,
      fields: [
        {
          field: 'applicationNames',
          displayValue: tr('Application'),
          dataType: Types.Array,
          entityConfig: {
            displayField: 'display',
            lookupField: 'name',
            completeMethod: (value: string) => {
              if (!value) {
                return of(this.allApplications);
              }
              return of(
                this.allApplications.filter((app) => {
                  return app.name.toUpperCase().includes(value.toUpperCase());
                }),
              );
            },
          },
        },
        {
          field: 'name',
          displayValue: tr('Name'),
          dataType: Types.String,
        },
        {
          field: 'clientId',
          displayValue: tr('Client'),
          dataType: Types.String,
        },

        {
          field: 'purchaseDate',
          displayValue: tr('Purchase date'),
          dataType: Types.Date,
          excludeFromGlobal: true,
        },
        {
          field: 'validFromDate',
          displayValue: tr('Valid from'),
          dataType: Types.Date,
          excludeFromGlobal: true,
        },
        {
          field: 'validUntilDate',
          displayValue: tr('Valid until'),
          dataType: Types.Date,
          excludeFromGlobal: true,
        },
        {
          field: 'quantity',
          displayValue: tr('Quantity'),
          dataType: Types.Integer,
          excludeFromGlobal: true,
        },
        {
          field: 'quantityAssigned',
          displayValue: tr('Quantity assigned'),
          dataType: Types.Integer,
          excludeFromGlobal: true,
        },
        {
          field: 'quantityAvailable',
          displayValue: tr('Quantity available'),
          dataType: Types.Integer,
          excludeFromGlobal: true,
        },
      ],
    });
  }

  onSearch(searchExpression: SearchExpression): void {
    this.searchExpression = searchExpression;
    this.filteredLicenses = [];
    this.licenses.forEach((license) => {
      if (this.searchExpression.checkItem(license.license)) {
        this.filteredLicenses.push(license);
      }
    });
  }
}
